import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

const wordV = {
  hidden: { opacity: 0, y: 28, filter: "blur(10px)" },
  show: (i) => ({ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease, delay: i * 0.07 } }),
};

const markV = {
  hidden: { scaleX: 0 },
  show: (i) => ({ scaleX: 1, transition: { duration: 0.7, ease, delay: i * 0.07 + 0.35 } }),
};

// Word-by-word blur reveal; `marker` paints a highlighter stroke behind the words.
export const SplitWords = ({ text, className = "", wordClass = "", inView = true, delay = 0, as = "span", marker }) => {
  const Tag = motion[as];
  const words = text.split(" ");
  const trigger = inView ? { whileInView: "show", viewport: { once: true, amount: 0.1 } } : { animate: "show" };
  return (
    <Tag className={className} initial="hidden" {...trigger} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} aria-hidden="true" className="relative inline-block">
          {marker && <motion.span variants={markV} custom={i + delay * 16} className="absolute inset-x-[-0.06em] bottom-[0.1em] -z-0 h-[0.34em] origin-left -rotate-1 rounded-sm" style={{ background: marker }} />}
          <motion.span className={`relative inline-block ${wordClass}`} variants={wordV} custom={i + delay * 16}>{w}</motion.span>
          {i < words.length - 1 && "\u00A0"}
        </span>
      ))}
    </Tag>
  );
};

export const FadeUp = ({ children, className = "", delay = 0, y = 40 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0.001, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.8, delay, ease }}
  >
    {children}
  </motion.div>
);

export const Parallax = ({ children, speed = 80, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  return <motion.div ref={ref} style={{ y }} className={className}>{children}</motion.div>;
};

// Pill button with shine sweep + press feedback.
export const Btn = ({ href, children, variant = "primary", testid, className = "", ...rest }) => {
  const styles = {
    primary: "bg-[#0055FF] text-white shadow-[0_14px_34px_rgba(0,85,255,0.35)] hover:bg-[#0043cc]",
    ghost: "border-2 border-slate-900 bg-white text-slate-900 hover:border-[#FF5500] hover:bg-[#FF5500] hover:text-white",
    dark: "bg-slate-900 text-white hover:bg-[#0055FF]",
  };
  return (
    <motion.a
      href={href}
      data-testid={testid}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-7 py-4 font-bold transition-colors duration-300 ${styles[variant]} ${className}`}
      {...rest}
    >
      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-white/30 opacity-0 transition-[transform,opacity] duration-700 group-hover:translate-x-[420%] group-hover:opacity-100" />
      <span className="relative inline-flex items-center gap-2">{children}</span>
    </motion.a>
  );
};

// CSS-only colour wipe on route enter; hidden entirely for reduced motion.
export const PageWipe = ({ color = "#0055FF" }) => (
  <div aria-hidden="true" className="page-wipe pointer-events-none fixed inset-0 z-[60]">
    <div className="page-wipe-a absolute inset-0" style={{ background: color }} />
    <div className="page-wipe-b absolute inset-0 bg-[#FACC15]" />
  </div>
);
